import {
  Check,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { isEqual, isNil } from "lodash";
import { useCallback, useMemo, useState } from "react";
import Txt from "../typography/Txt";
import colors from "../../theme/colors";

const TableSortDropdown = ({
  value,
  options, // Using same prop name as ToggleNavigation for consistency
  onChange,
  buttonText = "Sort By",
  color = "inherit",
  variant = "text",
  size = "small",
  useIndex = false,
  allowDeselect = false,
  sx = {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Same logic as ToggleNavigation for handling object vs string tabs
  const isObjectOptions = useMemo(
    () =>
      Array.isArray(options) &&
      options.length > 0 &&
      typeof options[0] === "object" &&
      "label" in options[0],
    [options]
  );

  const getTabLabel = useCallback(
    (tab) => (isObjectOptions ? tab.label : tab),
    [isObjectOptions]
  );

  const getTabValue = useCallback(
    (tab) => (isObjectOptions ? tab.value : tab),
    [isObjectOptions]
  );

  // Same value resolution logic as ToggleNavigation
  const _value = useMemo(() => {
    if (useIndex) return value;
    const idx = options.findIndex((tab) => isEqual(getTabValue(tab), value));
    return idx >= 0 ? idx : null;
  }, [useIndex, options, value, getTabValue]);

  // Get selected option for display
  const selectedOption = useMemo(() => {
    if (isNil(_value) || _value < 0 || _value >= options.length) return null;
    return options[_value];
  }, [_value, options]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (clickedIndex) => {
    if (isNil(clickedIndex)) return;

    // Handle deselection if allowed
    if (allowDeselect && _value === clickedIndex) {
      onChange(null);
      handleClose();
      return;
    }

    // Same change logic as ToggleNavigation
    if (useIndex) {
      onChange(clickedIndex);
    } else {
      onChange(getTabValue(options[clickedIndex]));
    }
    handleClose();
  };

  // Display text logic
  const displayText = useMemo(() => {
    if (selectedOption) {
      return getTabLabel(selectedOption);
    }
    return buttonText;
  }, [selectedOption, buttonText, getTabLabel]);

  const styles = useMemo(() => {
    let init = {
      borderRadius: 2,
      px: 1.5,
      textTransform: "none",
      justifyContent: "space-between",
      color: grey[600],
      fontSize: "11px !important",
      // backgroundColor: grey[100],
      border: `1px solid ${grey[300]}`,
    };

    if (selectedOption) {
      // init.backgroundColor = "#eef3ff";
      // init.backgroundColor = grey[100];
      // init.color = blue[700];
      init.color = colors.action;
      // init.color = grey[800];
      // init.border = `1px solid #2196B538`;
    }
    return init;
  }, [selectedOption]);

  const renderedIcon = useMemo(() => {
    if (selectedOption?.arrowDirection === "up") {
      return <KeyboardArrowUp />;
    }
    return <KeyboardArrowDown />;
  }, [selectedOption]);

  return (
    <Box sx={sx}>
      <Button
        variant={variant}
        size={size}
        color={selectedOption ? "info" : color}
        onClick={handleClick}
        endIcon={renderedIcon}
        sx={styles}
      >
        {displayText}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            mt: 1,
          },
        }}
      >
        {selectedOption ? (
          <>
            <Box sx={{ p: 1.5, pt: 1, pb: 1.5 }}>
              {/* <ListItemText primary={buttonText} /> */}
              <Txt bold>{buttonText}</Txt>
            </Box>
            <Divider />
          </>
        ) : null}
        {options.map((tab, index) => (
          <MenuItem
            key={index}
            onClick={() => handleSelect(index)}
            selected={_value === index}
            sx={{
              py: 1,
              px: 1.5,
              // py: 1.5,
              // px: 2,
            }}
          >
            <ListItemIcon
              sx={{
                // minWidth: 24,
                mr: -0.5,
                ".MuiSvgIcon-root": {
                  fontSize: "16px",
                  strokeWidth: 0.8,
                  stroke: "#ffffff",
                },
              }}
            >
              {tab.icon}
            </ListItemIcon>
            <ListItemText
              primary={getTabLabel(tab)}
              primaryTypographyProps={{
                variant: "body2",
              }}
            />
            {_value === index && (
              <ListItemIcon sx={{ minWidth: 24, ml: 2 }}>
                <Check fontSize="small" color="primary" />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}

        {/* Show deselect option if allowed and something is selected */}
        {/* {allowDeselect && !isNil(_value) && (
          <>
            <MenuItem
              onClick={() => handleSelect(null)}
              sx={{ py: 1.5, px: 2, fontStyle: "italic" }}
            >
              <ListItemText
                primary="Clear Selection"
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
              />
            </MenuItem>
          </>
        )} */}
      </Menu>
    </Box>
  );
};
export default TableSortDropdown;
